const BASE = process.env.NEXT_PUBLIC_API_SERVER;
const STORAGE_KEY_ACCESS_TOKEN = 'api:access_token';
const STORAGE_KEY_REFRESH_TOKEN = 'api:refresh_token';

export function logout() {
  localStorage.removeItem(STORAGE_KEY_ACCESS_TOKEN);
  localStorage.removeItem(STORAGE_KEY_REFRESH_TOKEN);
}

export async function requestLogin(
  username: string,
  password: string,
): Promise<{
  success: boolean;
  detail?: string;
}> {
  try {
    const data = new FormData();
    data.append('username', username);
    data.append('password', password);
    const res = await fetch(`${BASE}/api/auth/token/`, {
      method: 'POST',
      body: data,
    });
    if (res.status === 200) {
      const jsonRes = await res.json();
      localStorage.setItem(STORAGE_KEY_ACCESS_TOKEN, jsonRes.access_token);
      localStorage.setItem(STORAGE_KEY_REFRESH_TOKEN, jsonRes.refresh_token);
      return {
        success: true,
      };
    } else if (res.status === 401) {
      const jsonRes = await res.json();
      return {
        success: false,
        ...jsonRes,
      };
    } else {
      return {
        success: false,
        detail: 'unkown error',
      };
    }
  } catch (e) {
    let detail = 'unknown';
    if (String(e).indexOf('TypeError: Failed to fetch') !== -1) {
      detail = 'network error';
    }
    return {
      success: false,
      detail,
    };
  }
}

export class API {
  private getHeader() {
    const accessToken = localStorage.getItem(STORAGE_KEY_ACCESS_TOKEN);
    return {
      'Content-Type': 'application/json',
      ...(accessToken
        ? {
            Authorization: `Bearer ${accessToken}`,
          }
        : {}),
    };
  }

  async request<T>(
    url: string,
    method: string,
    data: any = undefined,
    depth: number = 0,
    signal?: AbortSignal, // 추가
  ): Promise<{ success: boolean; body?: T; detail?: string }> {
    try {
      if (depth > 3) {
        return { success: false, detail: 'max depth reached' };
      }
      const accessToken = localStorage.getItem(STORAGE_KEY_ACCESS_TOKEN);
      if (!accessToken) {
        return { success: false, detail: 'no access token' };
      }
      const headers = {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      };
      if (method === 'GET' && data) {
        url += '?' + new URLSearchParams(data).toString();
      }
      let res: Response;
      if (method === 'POST' || method === 'PUT' || method === 'PATCH') {
        if (data instanceof FormData) {
          res = await fetch(`${BASE}${url}`, {
            method,
            headers: { Authorization: `Bearer ${accessToken}` },
            body: data,
            signal,
          });
        } else {
          res = await fetch(`${BASE}${url}`, {
            method,
            headers,
            body: JSON.stringify(data),
            signal,
          });
        }
      } else {
        res = await fetch(`${BASE}${url}`, {
          method,
          headers,
          signal,
        });
      }
      if (res.status === 401) {
        const refreshToken = localStorage.getItem(STORAGE_KEY_REFRESH_TOKEN);
        if (refreshToken) {
          const formData = new FormData();
          formData.append('refresh_token', refreshToken);
          const refreshRes = await fetch(`${BASE}/api/auth/refresh/`, {
            method: 'POST',
            body: formData,
          });
          if (refreshRes.status === 200) {
            const jsonRes = await refreshRes.json();
            localStorage.setItem(STORAGE_KEY_ACCESS_TOKEN, jsonRes.access_token);
            localStorage.setItem(STORAGE_KEY_REFRESH_TOKEN, jsonRes.refresh_token);
            return this.request(url, method, data, depth + 1);
          } else if (refreshRes.status === 401) {
            localStorage.removeItem(STORAGE_KEY_ACCESS_TOKEN);
            localStorage.removeItem(STORAGE_KEY_REFRESH_TOKEN);
            return {
              success: false,
              detail: 'access token expired, refresh token expired',
            };
          } else {
            console.log('refresh else case', refreshRes);
            console.log('refresh else case', await refreshRes.json());
            return {
              success: false,
              detail: 'unknown',
            };
          }
        } else {
          localStorage.removeItem(STORAGE_KEY_ACCESS_TOKEN);
          localStorage.removeItem(STORAGE_KEY_REFRESH_TOKEN);
          return {
            success: false,
            detail: 'access token expired, no refresh token',
          };
        }
      } else if (res.status === 200) {
        return {
          success: true,
          body: await res.json(),
        };
      } else if (res.status === 204) {
        return {
          success: true,
        };
      } else {
        // Extract error message from HTTP error response
        try {
          const errorBody = await res.json();
          return {
            success: false,
            detail: errorBody.detail || errorBody.message || `HTTP ${res.status} Error`,
          };
        } catch {
          return {
            success: false,
            detail: `HTTP ${res.status} Error`,
          };
        }
      }
    } catch (e) {
      if (String(e).indexOf('AbortError') !== -1) {
        throw e; // AbortError는 그대로 전파
      }

      let detail = 'unknown';
      if (String(e).indexOf('TypeError: Failed to fetch') !== -1) {
        detail = 'network error';
      }
      console.log(e);
      return {
        success: false,
        detail,
      };
    }
  }

  async stream(url: string, data?: any, callback?: (res: any) => void) {
    try {
      const response = await fetch(`${BASE}${url}`, {
        method: 'POST',
        headers: this.getHeader(),
        body: JSON.stringify(data),
      });
      if (!response.ok || response.body === null) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const reader = response.body?.getReader();
      const decoder = new TextDecoder('utf-8');

      let buffer = '';
      while (true) {
        const { value, done } = await reader.read();
        if (done) {
          break;
        }
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split('\n\n');
        buffer = events.pop() || '';

        for (const eventBlock of events) {
          const lines = eventBlock.split('\n');
          let eventName = 'message';
          let eventData = '';

          for (const line of lines) {
            if (line.startsWith('event:')) {
              eventName = line.substring(6).trim(); // 'event:' 제거
            } else if (line.startsWith('data:')) {
              eventData += line.substring(5); // 'data:' 제거
            }
          }
          const responseEvent = {
            event: eventName,
            data: undefined,
          };
          if (eventData) {
            try {
              responseEvent.data = JSON.parse(eventData);
            } catch (e) {
              console.error('failed to parse json', e);
            }
          }
          if (callback) callback(responseEvent);
        }
      }
      return {
        success: true,
      };
    } catch (error) {
      console.error('Error:', error);
      return {
        success: false,
        detail: `${error}`,
      };
    }
  }

  static async get<T>(url: string, query?: any) {
    return new API().request<T>(url, 'GET', query);
  }

  static async post<T>(url: string, data?: any, options?: { signal?: AbortSignal }) {
    return new API().request<T>(url, 'POST', data, 0, options?.signal);
  }

  static async put<T>(url: string, data?: any) {
    return new API().request<T>(url, 'PUT', data);
  }

  static async patch<T>(url: string, data?: any) {
    return new API().request<T>(url, 'PATCH', data);
  }

  static async delete<T>(url: string) {
    return new API().request<T>(url, 'DELETE');
  }

  static async upload<T>(url: string, data?: any) {
    return new API().request<T>(url, 'PUT', data);
  }

  static async stream(url: string, data?: any, callback?: (res: any) => void) {
    return new API().stream(url, data, callback);
  }
}
